import {Schema} from "effect";

import type {ShortIdConfig, ShortIdData, ShortIdSchemaCompanion} from "./ShortId.js";
import {buildShortIdPattern, createShortIdSchemaCompanion} from "./ShortId.js";

// ── Account ShortId config ──────────────────────────────────────────────

/**
 * Config for account-level short IDs.
 *
 * Distinct from the project-level {@link ShortId} config in two ways:
 *
 * 1. `minLength: 3` — the first tier is 36³ = 46,656 values before the
 *    length grows to 4 (36⁴ = 1,679,616) and beyond. The algorithm
 *    auto-expands to the next tier once a tier's capacity is exhausted,
 *    so `minLength` is the floor for the earliest-minted IDs only. Minting
 *    in a deliberate paid-first / oldest-first order means the shortest
 *    IDs land on the most valued accounts.
 * 2. `hmacKey: "flux-account-short-id-v1"` — a distinct salt namespace
 *    means account short IDs and project short IDs never correlate, even
 *    when they share the same scope UID and counter value.
 */
const config: ShortIdConfig = {
    alphabet: "abcdefghijklmnopqrstuvwxyz0123456789",
    radix: 36,
    prefix: "~",
    minLength: 3,
    hmacKey: "flux-account-short-id-v1",
    feistelRounds: 8,
};

// ── Branded type via Effect Schema ──────────────────────────────────────

const accountShortIdPattern = buildShortIdPattern(config);

const schema = Schema.String.pipe(Schema.pattern(accountShortIdPattern), Schema.brand("AccountShortId"));

type AccountShortId = typeof schema.Type;

// ── Companion (schema-only) ─────────────────────────────────────────────

/**
 * Schema-only account {@link ShortIdSchemaCompanion}. Exposes the brand
 * schema, pattern, and refinement-isomorphism helpers. No crypto.
 *
 * To generate new account ShortIds, import the full companion of the
 * same name from `@buildwithflux/identifiers-logic`.
 */
const AccountShortId: ShortIdSchemaCompanion<AccountShortId> = createShortIdSchemaCompanion(
    config,
    schema,
    "AccountShortId",
);

/**
 * Convenience alias for the richer {@link ShortIdData} variant emitted by
 * the {@link AccountShortId} companion. Carries the `AccountShortId`
 * branded value type so consumers can keep the account/project
 * distinction at the type level.
 */
type AccountShortIdData<ScopeUid extends string = string> = ShortIdData<ScopeUid, AccountShortId>;

export {AccountShortId};
export type {AccountShortId as AccountShortIdType, AccountShortIdData};
